import { useQuery } from "@tanstack/react-query";
import {
  fetchStores,
  fetchStoresByCategory,
  fetchStoreInfo,
  fetchStoreCatalog,
  fetchLiquorStoreCatalog,
  fetchStoreRatingStats,
  fetchUserStoreRating,
} from "../../webService/webService";
import { queryKeys } from "../queryKeys";

/** All stores, unfiltered. */
export const useStores = (options = {}) =>
  useQuery({
    queryKey: queryKeys.stores.lists(),
    queryFn: fetchStores,
    ...options,
  });

/** Stores filtered by category (the customer-facing Store.jsx tabs). */
export const useStoresByCategory = (category, options = {}) =>
  useQuery({
    queryKey: queryKeys.stores.byCategory(category),
    queryFn: () => fetchStoresByCategory(category),
    enabled: Boolean(category),
    ...options,
  });

export const useStoreInfo = (storeId, options = {}) =>
  useQuery({
    queryKey: queryKeys.stores.detail(storeId),
    queryFn: () => fetchStoreInfo(storeId),
    enabled: Boolean(storeId),
    ...options,
  });

export const useStoreCatalog = (storeId, options = {}) =>
  useQuery({
    queryKey: queryKeys.catalog.byStore(storeId),
    queryFn: () => fetchStoreCatalog(storeId),
    enabled: Boolean(storeId),
    ...options,
  });

export const useLiquorStoreCatalog = (storeId, type, subType, options = {}) =>
  useQuery({
    queryKey: queryKeys.catalog.liquor(storeId, type, subType),
    queryFn: () => fetchLiquorStoreCatalog(storeId, type, subType),
    enabled: Boolean(storeId && type && subType),
    ...options,
  });

/** Average rating + count for a store. Short staleTime — kept fresh right after a rating mutation. */
export const useStoreRatingStats = (storeId, options = {}) =>
  useQuery({
    queryKey: queryKeys.ratings.stats(storeId),
    queryFn: () => fetchStoreRatingStats(storeId),
    enabled: Boolean(storeId),
    staleTime: 10_000,
    ...options,
  });

/** The signed-in user's own rating for a store — dependent on `uid` being resolved first. */
export const useUserStoreRating = (storeId, uid, options = {}) =>
  useQuery({
    queryKey: queryKeys.ratings.user(storeId, uid),
    queryFn: () => fetchUserStoreRating(storeId, uid),
    enabled: Boolean(storeId && uid),
    ...options,
  });
